import { applyOpacity } from "./utils";
import { css } from "styled-components";

/**
 * A subtle shadow used for elements that appear over the main app
 * (eg bottom sheet)
 */
const subtleShadow = css`
  box-shadow: 0px 0px 32px 0px
    ${({ theme }) => applyOpacity(theme.colours.dark, 0.1, false)};
`;

/**
 * WCAG 2.4.7 (Focus Visible) focus indicator: a two-colour ring drawn
 * entirely OUTSIDE the focused element, following the GOV.UK Design System
 * (https://design-system.service.gov.uk/get-started/focus-states/) and UK
 * Parliament Design System (https://designsystem.parliament.uk/foundations/focus-state/)
 * "double box-shadow ring" pattern.
 *
 * Replaces the previous `whiteInsetBorderShadow` + brand-colour outline
 * pattern, which drew the indicator INSIDE the element using a colour
 * derived from the button's own brand colour (e.g. `--d-tint`/`--d-primary`)
 * — meaning its visibility depended entirely on that brand colour, and was
 * barely visible on some real brand colours (e.g. pale pink, amber) and on
 * dark-theme surfaces.
 *
 * Uses FIXED pure white/black (not theme tokens like --d-surface/--d-text)
 * deliberately: at least one of {white, black} is mathematically guaranteed
 * to clear the WCAG 3:1 non-text/UI-component contrast threshold against
 * ANY background colour (proof: for a background of relative luminance L,
 * contrast-with-white < 3 requires L > 0.30, and contrast-with-black < 3
 * requires L < 0.10 — these can't both be true at once, so at least one
 * ring always clears 3:1). Using theme tokens instead risks the ring colour
 * coincidentally matching the element's own fill (e.g. a dark-theme surface
 * colour matching a dark-theme button background), making that ring
 * invisible. See boxShadows.test.ts's focus-ring contrast matrix for a
 * concrete numeric proof across representative brand colours (navy, pink,
 * amber) in both themes.
 *
 * Box-shadow (not `outline`) is used so the ring is never clipped by
 * `border-radius`, and both rings sit outside the border box (never inset /
 * occluded by the element's own background).
 *
 * No transition/animation is applied here (and none should be added) — the
 * app-wide `prefers-reduced-motion: reduce` override in styles/tailwind.css
 * neutralises transitions/animations globally, so this indicator should
 * always appear instantly regardless of that override.
 */
/**
 * The two fixed ring colours used by `focusRing` (exported so tests can
 * assert contrast against them directly, without duplicating/hardcoding
 * the literal hex values — see boxShadows.test.ts).
 */
export const FOCUS_RING_LIGHT_COLOUR = "#ffffff";
export const FOCUS_RING_DARK_COLOUR = "#000000";

const focusRing = css`
  outline: none;
  box-shadow:
    0 0 0 2px ${FOCUS_RING_LIGHT_COLOUR},
    0 0 0 4px ${FOCUS_RING_DARK_COLOUR};
`;

export const boxShadows = {
  subtleShadow,
  focusRing,
} as const;
